import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateSchoolSpeakerMutation } from '@/api/endpoints/schools/schoolSubjectionParams/speakers';
import { DataSection, ImageUploadField, TextAreaField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { SPEAKER_VALIDATION_SCHEMA } from '../../consts';
import type { ISpeakerItemData, TFormData } from '../../types';

import styles from './SpeakerModals.module.scss';

type TProps = {
  schoolId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
  handleClearOperateItem: VoidFunction;
  itemToOperate: ISpeakerItemData;
};

export const EditSpeakerModal = ({
  schoolId,
  isModalOpen,
  handleCancel,
  handleClearOperateItem,
  itemToOperate,
}: TProps) => {
  const [editSpeaker, { isLoading: isSpeakerUpdating }] = useUpdateSchoolSpeakerMutation();
  const sendApiRequest = useApiRequest();

  const methods = useForm<TFormData>({
    defaultValues: {
      fullname: itemToOperate ? itemToOperate.full_name : '',
      bio: itemToOperate ? itemToOperate.bio : '',
      photo: itemToOperate ? itemToOperate.photo : null,
    },
    resolver: yupResolver(SPEAKER_VALIDATION_SCHEMA),
  });

  const { watch, handleSubmit, setValue } = methods;

  const [isImageChanged, setIsImageChanged] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const photo = watch('photo');

  const getImageName = () => {
    if (!photo) {
      return '';
    }

    return photo.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setValue('photo', null);
    setImageUrl('');
    setIsImageChanged(false);
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  const imageChangeCallback = useCallback(() => setIsImageChanged(true), []);

  const handleSaveSpeaker = handleSubmit(({ fullname, bio, photo }) => {
    sendApiRequest({
      successMessage: 'Спикер успешно изменён',
      apiRequestCallback: () =>
        editSpeaker({
          speakerId: itemToOperate.id,
          schoolId,
          data: {
            full_name: fullname,
            job_title: 'Спикер',
            bio,
            ...(photo?.signed_id && { photo: photo.signed_id }),
          },
        }),
      successCallback: () => {
        handleCancel();
        handleClearOperateItem();
      },
    });
  });

  useEffect(() => {
    if (!photo?.url) {
      return;
    }

    if (!isImageChanged) {
      setImageUrl(photo.url);
    }
  }, [isImageChanged, photo]);

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isSpeakerUpdating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Изменение спикера</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="ФИО"
                titleClassName={styles.fullname}
                isTitleRequired
              >
                <TextAreaField
                  fieldName="fullname"
                  placeholder="ФИО"
                />
              </DataSection>
              <DataSection
                titleText="Описание"
                titleClassName={styles.description}
                isTitleRequired
              >
                <TextAreaField
                  fieldName="bio"
                  placeholder="Описание"
                />
              </DataSection>
              <DataSection
                titleText="Фото"
                isTitleRequired
              >
                <ImageUploadField
                  accept="image/png,image/jpeg,image/jpg"
                  name="Изображение"
                  fieldName="photo"
                  className={photo ? styles['image-uploaded'] : ''}
                  requestCallback={imageRequestCallback}
                  changeCallback={imageChangeCallback}
                  showUploadList={false}
                />
                {photo && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setIsImagePreviewOpen(true)}
                      type="text"
                      icon={<PaperClipOutlined />}
                      className={styles['image-button-wrapper']}
                    >
                      {getImageName()}
                      <DeleteOutlined
                        className={styles['remove-image-icon']}
                        onClick={removeUploadedImage}
                      />
                    </Button>
                    <Image
                      alt="Фото спикера"
                      style={{ display: 'none' }}
                      preview={{
                        open: isImagePreviewOpen,
                        src: imageUrl,
                        onOpenChange: (value) => setIsImagePreviewOpen(value),
                      }}
                    />
                  </>
                )}
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                type="dashed"
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleSaveSpeaker}
                type="primary"
                className={styles.button}
                disabled={isSpeakerUpdating}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
